import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { BaseInputTemplateProps, FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils'
import { connect, Formik, FormikValues } from 'formik'
// @ts-expect-error
import { buildYup } from 'json-schema-to-yup'
import { cloneDeep, findKey, isEmpty, omit, startCase } from 'lodash'
import { RefObject, useMemo } from 'react'
import { IntlShape } from 'react-intl'
import { useQueries, useQuery, UseQueryOptions } from 'react-query'
import { useParams } from 'react-router-dom'
import { Divider } from 'semantic-ui-react'
import styled from 'styled-components'

import InlineLoader from '@/components/InlineLoader'
import { WorkgridDatasourceConfiguration } from '@/pages/SpaceAppBuilder/AppDetailsPage/interfaces'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import { WorkgridDatasourceForm } from '@/pages/SpaceAppBuilder/components/WorkgridDatasourceForm'
import { extractUiSchema } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Form'
import { kebabToCapitalized } from '@/pages/SpaceAppBuilder/FeaturesPage/utils'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  App,
  AppInstanceDataSource,
  DataSourceStepData,
  FieldTypes,
  StepData,
  WorkgridDatasource,
  WorkgridDatasourceStepData
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import {
  dataSourceV3QueryOptions,
  getDataSourceConfigurationQueryOptions,
  getFetchAppQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'

//<editor-fold desc="Styled Components">
const Description = styled.p`
  margin: 0px 0 10px;
  font-size: 0.9rem;
  color: #6c6c6c;
`
//</editor-fold>

//<editor-fold desc="Interfaces">
type WorkgridDataSourceProps = {
  readOnly?: boolean
  submitFormRef?: RefObject<HTMLButtonElement>
}
//</editor-fold>

const getValidationSchema = (intl: IntlShape, formObject: any, inputSchema: any) => {
  if (inputSchema) {
    const yupSchema = buildYup(inputSchema)
    return {
      inputs: yupSchema
    }
  } else {
    return null
  }
}

const convertStepData = (stepData: DataSourceStepData, datasourceDocs: WorkgridDatasourceConfiguration) => {
  if (stepData.path) {
    const legacyStepData = cloneDeep(stepData)

    const workgridStepAction = findKey(
      datasourceDocs.actions,
      value => value.legacy?.path === legacyStepData.path && value.legacy?.method === legacyStepData.method
    )
    if (!workgridStepAction) return
    const convertedStepData = { inputs: {}, dataSourceKey: datasourceDocs.id, action: workgridStepAction }
    if (legacyStepData.input && datasourceDocs.actions[workgridStepAction]?.inputs) {
      const params = legacyStepData.input?.query?.params?.reduce(
        (acc: { [x: string]: any }, item: { key: string | number; value: any }) => {
          acc[item.key] = item.value
          return acc
        },
        {}
      )

      convertedStepData.inputs = {
        ...(legacyStepData.input.query && omit(legacyStepData.input.query, 'params')),
        ...(legacyStepData.input.body && legacyStepData.input.body),
        ...(legacyStepData.input.path && legacyStepData.input.path),
        params
      }
      return convertedStepData
    }
  }
}

const getInitialValues = (stepData: WorkgridDatasourceStepData): StepData => ({
  inputs: stepData.inputs ?? {},
  dataSourceKey: stepData.dataSourceKey,
  action: stepData.action
})

const getNetworkRequestObject = (data: { triggerData: any; stepData: any }) => {
  const updatedValues: any = cloneDeep(data)
  const { stepData } = data
  if (isEmpty(stepData)) return data
  updatedValues.stepData = {
    inputs: stepData.inputs ?? {},
    dataSourceKey: stepData.dataSourceKey,
    action: stepData.action
  }

  return updatedValues
}

const WorkgridDatasourceOperation = ({
  formik,
  readOnly = false,
  submitFormRef
}: WorkgridDataSourceProps & FormikValues) => {
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()
  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()
  const widgets = useMemo(() => {
    return {
      TextareaWidget: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
        props: WidgetProps<T, S, F>
      ) => {
        const { disabled, onChange, name, value } = props
        return (
          <div className="w-full min-w-[280px]" key={name}>
            <Formik initialValues={{ [name]: value }} onSubmit={() => {}}>
              <VariableInput
                key={name}
                name={name}
                onChange={onChange}
                multiline={true}
                validVariableTypes={[
                  FieldTypes.ARRAY,
                  FieldTypes.OBJECT,
                  FieldTypes.STRING,
                  FieldTypes.BOOLEAN,
                  FieldTypes.INTEGER,
                  FieldTypes.NUMBER,
                  FieldTypes.ANY
                ]}
                variables={variables}
                readOnly={disabled || readOnly}
              />
            </Formik>
          </div>
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]) // this is here to retain referential integrity because variables is a new object in every render, expecting variables to be unchanged while the form is open
  const baseTemplate = useMemo(() => {
    return {
      BaseInputTemplate: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
        props: BaseInputTemplateProps<T, S, F>
      ) => {
        const { disabled, onChange, name, value } = props
        return (
          <div className="w-full min-w-[280px]" key={name}>
            <Formik initialValues={{ [name]: value }} onSubmit={() => {}}>
              <VariableInput
                key={name}
                name={name}
                onChange={onChange}
                multiline={true}
                validVariableTypes={[
                  FieldTypes.ARRAY,
                  FieldTypes.OBJECT,
                  FieldTypes.STRING,
                  FieldTypes.BOOLEAN,
                  FieldTypes.INTEGER,
                  FieldTypes.NUMBER,
                  FieldTypes.ANY
                ]}
                variables={variables}
                readOnly={disabled || readOnly}
              />
            </Formik>
          </div>
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]) // this is here to retain referential integrity because variables is a new object in every render, expecting variables to be unchanged while the form is open

  const appQueryResult = useQuery<App>({
    ...getFetchAppQueryOptions({ spaceId, appId })
  })

  const applicationDataSources: AppInstanceDataSource[] = appQueryResult?.data?.dataSources || []
  type WorkgridDatasourceInstanceQueries = UseQueryOptions<WorkgridDatasource>[]
  const dataSourcesQuery = useQueries<WorkgridDatasourceInstanceQueries>(
    applicationDataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3QueryOptions({
        spaceId,
        datasourceId: ds.datasourceId || ds.key,
        instanceId: ds.id
      })
    })) || []
  )

  const dataSources = dataSourcesQuery.reduce((dataSourcesInstances: WorkgridDatasource[], dataSource) => {
    if (dataSource.data) {
      dataSourcesInstances.push(dataSource.data)
    }
    return dataSourcesInstances
  }, [])

  const datasourceDocsResult =
    useQuery<WorkgridDatasourceConfiguration>({
      ...getDataSourceConfigurationQueryOptions({
        spaceId,
        dataSourceId: formik.values?.stepData?.dataSourceKey
      }),
      enabled: !!formik.values?.stepData?.dataSourceKey
    }) ?? {}

  return (
    <div className="mb-[50px] h-full" data-testid="log-property-panel">
      <InlineLoader useQueryResult={datasourceDocsResult}>
        {datasourceDocs => {
          const resource = formik.values.stepData?.action?.split('/')?.[1] || startCase(formik.values.stepData?.action)
          const action = datasourceDocs.actions?.[formik.values.stepData?.action]
          const description =
            formik.values.stepData?.action === '_ping'
              ? 'The ping operation is used to verify and keep the datasource connection alive'
              : action?.description
          const selectedDataSource = dataSources?.find(ds => ds.datasourceId === formik.values.stepData.dataSourceKey)
          const baseUrl =
            formik.values.stepData?.action === '_proxy'
              ? datasourceDocs.schema.properties['baseUrl']?.default?.replace(
                  new RegExp(/\$?{([^}]+)}/g, 'gi'),
                  (matched: string) => {
                    const matchedPlaceholder = matched.replaceAll(/[${}]/g, '')
                    return selectedDataSource?.configuration[matchedPlaceholder] || matched
                  }
                )
              : null
          return (
            <>
              <div className="flex-start flex items-start justify-between">
                <div>
                  <div className="inline-flex">
                    <p className="block text-sm font-bold leading-6 text-gray-900">Action: </p>
                    <p className="block pl-2 text-sm font-normal leading-6 text-gray-900">
                      {action?.name || kebabToCapitalized(resource)}
                    </p>
                  </div>
                  {description && <Description>{description}</Description>}
                </div>
                {datasourceDocs?.vendorApiUrl && (
                  <a
                    href={datasourceDocs?.vendorApiUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="flex items-center font-medium text-blue-500 hover:underline dark:text-blue-500"
                  >
                    Vendor API Documentation
                    <ArrowTopRightOnSquareIcon
                      aria-hidden="true"
                      className="ml-1.5 mb-0.5 h-4 w-4 flex-shrink-0 text-blue-500 hover:underline dark:text-blue-500"
                    />
                  </a>
                )}
              </div>
              <Divider />
              {baseUrl && (
                <div className="px-4">
                  <p className="block text-sm font-bold leading-6 text-gray-900">Base URL: </p>
                  <p className="block text-[13px] font-normal leading-6 text-gray-500">{baseUrl}</p>
                </div>
              )}
              <WorkgridDatasourceForm
                className="builder-form group h-full p-0"
                templates={baseTemplate}
                widgets={widgets}
                uiSchema={extractUiSchema(action?.inputs || {})}
                initialFormData={formik.values.stepData.inputs}
                onChange={event => formik.setFieldValue('stepData.inputs', event)}
                schema={action?.inputs || {}}
              >
                <button ref={submitFormRef} type="submit" style={{ display: 'none' }} />
              </WorkgridDatasourceForm>
            </>
          )
        }}
      </InlineLoader>
    </div>
  )
}

export default connect(WorkgridDatasourceOperation)

export { getInitialValues, getValidationSchema, getNetworkRequestObject, convertStepData }
